import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, Timestamp, orderBy } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { habitaciones } from '../data/habitaciones';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Area, AreaChart
} from 'recharts';
import './AdminDashboard.css';
import FloatingMenu from './FloatingMenu';

const AdminDashboard = () => {
  const [reservasHoy, setReservasHoy] = useState([]);
  const [reservasSemana, setReservasSemana] = useState([]);
  const [ingresosMensuales, setIngresosMensuales] = useState([]);
  const [ingresosPorHabitacion, setIngresosPorHabitacion] = useState([]);
  const [ocupacionPorHabitacion, setOcupacionPorHabitacion] = useState([]);
  const [personasPorMes, setPersonasPorMes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [anioActual] = useState(new Date().getFullYear());
  const navigate = useNavigate();

  // Colores para los gráficos
  const COLORS = [
    '#8884d8', '#83a6ed', '#8dd1e1', '#82ca9d', '#a4de6c',
    '#d0ed57', '#ffc658', '#ff8042', '#ff6361', '#bc5090'
  ];

  // Array de nombres de meses
  const meses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Cargar datos al inicio
  useEffect(() => {
    cargarDatos();
  }, []);

  // Función para cargar todos los datos
  const cargarDatos = async () => {
    try {
      setLoading(true);
      setError(null);

      // Obtener todas las reservas
      const reservasCollection = collection(db, 'reservas');
      const reservasSnapshot = await getDocs(reservasCollection);
      const todasReservas = reservasSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filtrar para excluir las reservas bloqueadas
      const reservasValidas = todasReservas.filter(reserva => {
        const status = reserva.status;
        const nombre = reserva.nombre || reserva.name || '';
        return status !== 'blocked' && nombre !== 'Bloqueado';
      });

      console.log(`Procesando ${reservasValidas.length} reservas válidas`);

      // Procesar reservas de hoy
      procesarReservasHoy(reservasValidas);
      
      // Procesar reservas de la semana
      procesarReservasSemana(reservasValidas);
      
      // Procesar datos para gráficos
      procesarIngresosMensuales(reservasValidas);
      procesarIngresosPorHabitacion(reservasValidas);
      calcularOcupacionMensual(reservasValidas);
      calcularPersonasPorMes(reservasValidas);

      setLoading(false);
    } catch (err) {
      console.error("Error al cargar datos:", err);
      setError("Error al cargar datos. Por favor, intente nuevamente.");
      setLoading(false);
    }
  };

  // Procesar reservas del día actual
  const procesarReservasHoy = (reservas) => {
    const fechaHoy = new Date();
    fechaHoy.setHours(0, 0, 0, 0); // Inicio del día

    // Filtrar reservas en curso hoy (incluye check-in, check-out y estadías en progreso)
    const reservasDelDia = reservas.filter(reserva => {
      let fechaEntrada, fechaSalida;
      
      // Procesar fechaEntrada
      if (reserva.fechaEntrada instanceof Timestamp) {
        fechaEntrada = reserva.fechaEntrada.toDate();
      } else if (typeof reserva.fechaEntrada === 'object' && reserva.fechaEntrada.seconds) {
        fechaEntrada = new Date(reserva.fechaEntrada.seconds * 1000);
      } else if (reserva.fechaEntrada instanceof Date) {
        fechaEntrada = reserva.fechaEntrada;
      } else {
        fechaEntrada = new Date(reserva.fechaEntrada || reserva.checkIn);
      }
      
      // Procesar fechaSalida
      if (reserva.fechaSalida instanceof Timestamp) {
        fechaSalida = reserva.fechaSalida.toDate();
      } else if (typeof reserva.fechaSalida === 'object' && reserva.fechaSalida.seconds) {
        fechaSalida = new Date(reserva.fechaSalida.seconds * 1000);
      } else if (reserva.fechaSalida instanceof Date) {
        fechaSalida = reserva.fechaSalida;
      } else {
        fechaSalida = new Date(reserva.fechaSalida || reserva.checkOut);
      }
      
      // Normalizar a inicio del día para comparación
      fechaEntrada.setHours(0, 0, 0, 0);
      fechaSalida.setHours(0, 0, 0, 0);
      
      // Es check-in, check-out, o estadía en curso hoy
      return fechaEntrada.getTime() === fechaHoy.getTime() || // check-in hoy
             fechaSalida.getTime() === fechaHoy.getTime() || // check-out hoy
             (fechaEntrada.getTime() < fechaHoy.getTime() && fechaSalida.getTime() > fechaHoy.getTime()); // estadía en curso
    });

    // Añadir tipo (check-in/check-out/estadía) y ordenar por habitación
    const reservasConTipo = reservasDelDia.map(reserva => {
      let fechaEntrada, fechaSalida;
      
      if (reserva.fechaEntrada instanceof Timestamp) {
        fechaEntrada = reserva.fechaEntrada.toDate();
      } else if (typeof reserva.fechaEntrada === 'object' && reserva.fechaEntrada.seconds) {
        fechaEntrada = new Date(reserva.fechaEntrada.seconds * 1000);
      } else if (reserva.fechaEntrada instanceof Date) {
        fechaEntrada = reserva.fechaEntrada;
      } else {
        fechaEntrada = new Date(reserva.fechaEntrada || reserva.checkIn);
      }
      
      if (reserva.fechaSalida instanceof Timestamp) {
        fechaSalida = reserva.fechaSalida.toDate();
      } else if (typeof reserva.fechaSalida === 'object' && reserva.fechaSalida.seconds) {
        fechaSalida = new Date(reserva.fechaSalida.seconds * 1000);
      } else if (reserva.fechaSalida instanceof Date) {
        fechaSalida = reserva.fechaSalida;
      } else {
        fechaSalida = new Date(reserva.fechaSalida || reserva.checkOut);
      }
      
      fechaEntrada.setHours(0, 0, 0, 0);
      fechaSalida.setHours(0, 0, 0, 0);
      
      // Determinar tipo de evento
      let tipoEvento;
      if (fechaEntrada.getTime() === fechaHoy.getTime()) {
        tipoEvento = 'check-in';
      } else if (fechaSalida.getTime() === fechaHoy.getTime()) {
        tipoEvento = 'check-out';
      } else {
        tipoEvento = 'estadia';
      }
      
      return {
        ...reserva,
        tipoEvento
      };
    });

    // Ordenar por habitacionId
    reservasConTipo.sort((a, b) => {
      const habitacionA = a.habitacionId || a.roomId || 0;
      const habitacionB = b.habitacionId || b.roomId || 0;
      return habitacionA - habitacionB;
    });

    setReservasHoy(reservasConTipo);
  };

  // Procesar reservas de la próxima semana
  const procesarReservasSemana = (reservas) => {
    const fechaHoy = new Date();
    fechaHoy.setHours(0, 0, 0, 0);
    
    const fechaFinSemana = new Date(fechaHoy);
    fechaFinSemana.setDate(fechaFinSemana.getDate() + 7);
    
    // Filtrar reservas de los próximos 7 días
    const reservasProximaSemana = reservas.filter(reserva => {
      let fechaEntrada;
      
      if (reserva.fechaEntrada instanceof Timestamp) {
        fechaEntrada = reserva.fechaEntrada.toDate();
      } else if (typeof reserva.fechaEntrada === 'object' && reserva.fechaEntrada.seconds) {
        fechaEntrada = new Date(reserva.fechaEntrada.seconds * 1000);
      } else if (reserva.fechaEntrada instanceof Date) {
        fechaEntrada = reserva.fechaEntrada;
      } else {
        fechaEntrada = new Date(reserva.fechaEntrada || reserva.checkIn);
      }
      
      fechaEntrada.setHours(0, 0, 0, 0);
      
      // Es check-in en la próxima semana pero no hoy
      return fechaEntrada > fechaHoy && fechaEntrada <= fechaFinSemana;
    });

    // Ordenar por fecha de entrada
    reservasProximaSemana.sort((a, b) => {
      let fechaA, fechaB;
      
      if (a.fechaEntrada instanceof Timestamp) {
        fechaA = a.fechaEntrada.toDate();
      } else if (typeof a.fechaEntrada === 'object' && a.fechaEntrada.seconds) {
        fechaA = new Date(a.fechaEntrada.seconds * 1000);
      } else if (a.fechaEntrada instanceof Date) {
        fechaA = a.fechaEntrada;
      } else {
        fechaA = new Date(a.fechaEntrada || a.checkIn);
      }
      
      if (b.fechaEntrada instanceof Timestamp) {
        fechaB = b.fechaEntrada.toDate();
      } else if (typeof b.fechaEntrada === 'object' && b.fechaEntrada.seconds) {
        fechaB = new Date(b.fechaEntrada.seconds * 1000);
      } else if (b.fechaEntrada instanceof Date) {
        fechaB = b.fechaEntrada;
      } else {
        fechaB = new Date(b.fechaEntrada || b.checkIn);
      }
      
      return fechaA - fechaB;
    });

    setReservasSemana(reservasProximaSemana);
  };

  // Procesar ingresos mensuales para el gráfico
  const procesarIngresosMensuales = (reservas) => {
    // Inicializar objeto para almacenar ingresos por mes
    const ingresosPorMes = {};
    
    // Inicializar todos los meses con cero
    for (let i = 0; i < 12; i++) {
      ingresosPorMes[i] = 0;
    }

    // Calcular ingresos por mes del año actual
    reservas.forEach(reserva => {
      // Obtener fecha de entrada
      let fechaEntrada;
      if (reserva.fechaEntrada instanceof Timestamp) {
        fechaEntrada = reserva.fechaEntrada.toDate();
      } else if (typeof reserva.fechaEntrada === 'object' && reserva.fechaEntrada.seconds) {
        fechaEntrada = new Date(reserva.fechaEntrada.seconds * 1000);
      } else if (reserva.fechaEntrada instanceof Date) {
        fechaEntrada = reserva.fechaEntrada;
      } else {
        fechaEntrada = new Date(reserva.fechaEntrada || reserva.checkIn);
      }

      // Si la fecha es del año actual, sumar al mes correspondiente
      if (fechaEntrada.getFullYear() === anioActual) {
        const mes = fechaEntrada.getMonth();
        const precioTotal = reserva.precioTotal || reserva.totalPrice || 0;
        
        ingresosPorMes[mes] += parseInt(precioTotal);
      }
    });

    // Convertir a formato para recharts
    const datosParaGrafico = Object.keys(ingresosPorMes).map(mes => ({
      name: meses[mes],
      ingresos: ingresosPorMes[mes]
    }));

    setIngresosMensuales(datosParaGrafico);
  };

  // Procesar ingresos por habitación para el gráfico
  const procesarIngresosPorHabitacion = (reservas) => {
    // Obtener mes y año actual
    const mesActual = new Date().getMonth();
    const anioActual = new Date().getFullYear();
    
    // Inicializar objeto para almacenar ingresos por habitación
    const ingresosPorHab = {};
    
    // Inicializar todas las habitaciones
    habitaciones.forEach(hab => {
      ingresosPorHab[hab.id] = {
        nombre: hab.nombre,
        ingresos: 0
      };
    });

    // Calcular ingresos por habitación (solo del mes actual)
    reservas.forEach(reserva => {
      const habitacionId = reserva.habitacionId || reserva.roomId;
      
      // Obtener fecha de entrada
      let fechaEntrada;
      if (reserva.fechaEntrada instanceof Timestamp) {
        fechaEntrada = reserva.fechaEntrada.toDate();
      } else if (typeof reserva.fechaEntrada === 'object' && reserva.fechaEntrada.seconds) {
        fechaEntrada = new Date(reserva.fechaEntrada.seconds * 1000);
      } else if (reserva.fechaEntrada instanceof Date) {
        fechaEntrada = reserva.fechaEntrada;
      } else {
        fechaEntrada = new Date(reserva.fechaEntrada || reserva.checkIn);
      }
      
      // Solo considerar reservas del mes actual
      if (habitacionId && ingresosPorHab[habitacionId] && 
          fechaEntrada.getMonth() === mesActual && 
          fechaEntrada.getFullYear() === anioActual) {
        const precioTotal = reserva.precioTotal || reserva.totalPrice || 0;
        ingresosPorHab[habitacionId].ingresos += parseInt(precioTotal);
      }
    });

    // Convertir a formato para recharts
    const datosParaGrafico = Object.keys(ingresosPorHab)
      .filter(id => ingresosPorHab[id].ingresos > 0) // Solo incluir habitaciones con ingresos
      .map(id => ({
        name: `Hab. ${id}`,
        ingresos: ingresosPorHab[id].ingresos,
        nombreCompleto: ingresosPorHab[id].nombre
      }));

    setIngresosPorHabitacion(datosParaGrafico);
  };

  // Nueva función para calcular la ocupación mensual por habitación
  const calcularOcupacionMensual = (reservas) => {
    const mesActual = new Date().getMonth();
    const anioActual = new Date().getFullYear();
    const diasEnMes = new Date(anioActual, mesActual + 1, 0).getDate();
    
    // Inicializar objeto para almacenar días ocupados por habitación
    const ocupacionHabitaciones = {};
    
    // Inicializar todas las habitaciones
    habitaciones.forEach(hab => {
      ocupacionHabitaciones[hab.id] = {
        id: hab.id,
        nombre: hab.nombre,
        diasOcupados: 0,
        ocupacion: 0
      };
    });
    
    // Analizar cada reserva
    reservas.forEach(reserva => {
      try {
        // Extraer fechas considerando diferentes estructuras de datos
        let fechaEntrada, fechaSalida;
        
        // Intentar obtener fechas de diferentes propiedades
        if (reserva.fechaEntrada) {
          fechaEntrada = obtenerFechaDesdeReserva(reserva.fechaEntrada);
        } else if (reserva.checkIn) {
          fechaEntrada = obtenerFechaDesdeReserva(reserva.checkIn);
        } else if (reserva.datosComunes && reserva.datosComunes.fechaEntrada) {
          fechaEntrada = obtenerFechaDesdeReserva(reserva.datosComunes.fechaEntrada);
        }
        
        if (reserva.fechaSalida) {
          fechaSalida = obtenerFechaDesdeReserva(reserva.fechaSalida);
        } else if (reserva.checkOut) {
          fechaSalida = obtenerFechaDesdeReserva(reserva.checkOut);
        } else if (reserva.datosComunes && reserva.datosComunes.fechaSalida) {
          fechaSalida = obtenerFechaDesdeReserva(reserva.datosComunes.fechaSalida);
        }
        
        // Si no hay fechas válidas, omitir esta reserva
        if (!fechaEntrada || !fechaSalida) {
          console.warn(`Reserva ${reserva.id} sin fechas válidas`);
          return;
        }
        
        // Normalizar a inicio del día
        fechaEntrada.setHours(0, 0, 0, 0);
        fechaSalida.setHours(0, 0, 0, 0);
        
        // Verificar si la reserva está en el mes actual
        const inicioMes = new Date(anioActual, mesActual, 1);
        const finMes = new Date(anioActual, mesActual + 1, 0);
        
        // Si la reserva no intersecta con el mes actual, omitirla
        if (fechaSalida < inicioMes || fechaEntrada > finMes) {
          return;
        }
        
        // Calcular intervalo de ocupación dentro del mes
        const inicioOcupacion = fechaEntrada < inicioMes ? inicioMes : fechaEntrada;
        const finOcupacion = fechaSalida > finMes ? finMes : fechaSalida;
        
        // Calcular días ocupados (hasta fecha de salida - 1 ya que el check-out no se cuenta como día ocupado)
        const diasOcupados = Math.max(0, Math.floor((finOcupacion - inicioOcupacion) / (1000 * 60 * 60 * 24)));
        
        // Obtener habitaciones de esta reserva
        let habitacionesReserva = [];
        
        if (reserva.habitaciones && Array.isArray(reserva.habitaciones)) {
          // Formato: reserva.habitaciones[].habitacionId
          habitacionesReserva = reserva.habitaciones.map(h => h.habitacionId || h.id).filter(Boolean);
        } else if (reserva.habitacionId) {
          // Formato: reserva.habitacionId
          habitacionesReserva = [reserva.habitacionId];
        } else if (reserva.roomId) {
          // Formato: reserva.roomId
          habitacionesReserva = [reserva.roomId];
        }
        
        // Actualizar días ocupados para cada habitación en esta reserva
        habitacionesReserva.forEach(habId => {
          const habitacionId = habId.toString();
          if (ocupacionHabitaciones[habitacionId]) {
            ocupacionHabitaciones[habitacionId].diasOcupados += diasOcupados;
          }
        });
      } catch (err) {
        console.warn(`Error al procesar reserva ${reserva.id}:`, err);
      }
    });
    
    // Calcular porcentaje de ocupación para cada habitación
    Object.values(ocupacionHabitaciones).forEach(hab => {
      hab.ocupacion = Math.round((hab.diasOcupados / diasEnMes) * 100);
    });
    
    // Convertir a array y ordenar por ID
    const datosOcupacion = Object.values(ocupacionHabitaciones)
      .filter(hab => hab.id) // Filtrar habitaciones sin ID
      .sort((a, b) => a.id - b.id);
    
    setOcupacionPorHabitacion(datosOcupacion);
  };
  
  // Función auxiliar para obtener una fecha a partir de diferentes formatos
  const obtenerFechaDesdeReserva = (fechaInput) => {
    if (!fechaInput) return null;
    
    try {
      if (fechaInput instanceof Timestamp) {
        return fechaInput.toDate();
      } else if (typeof fechaInput === 'object' && fechaInput.seconds) {
        return new Date(fechaInput.seconds * 1000);
      } else if (fechaInput instanceof Date) {
        return fechaInput;
      } else {
        return new Date(fechaInput);
      }
    } catch (err) {
      console.warn("Error al convertir fecha:", err);
      return null;
    }
  };

  // Formatear fecha
  const formatearFecha = (fecha) => {
    if (!fecha) return '';
    
    let fechaObj;
    if (fecha instanceof Timestamp) {
      fechaObj = fecha.toDate();
    } else if (typeof fecha === 'object' && fecha.seconds) {
      fechaObj = new Date(fecha.seconds * 1000);
    } else if (fecha instanceof Date) {
      fechaObj = fecha;
    } else {
      fechaObj = new Date(fecha);
    }
    
    return fechaObj.toLocaleDateString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Obtener el nombre de la habitación por ID
  const obtenerNombreHabitacion = (id) => {
    const habitacion = habitaciones.find(h => h.id.toString() === id.toString());
    return habitacion ? habitacion.nombre : `Habitación ${id}`;
  };

  // Formatear moneda
  const formatearMoneda = (valor) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(valor);
  };

  // Personalizar tooltip del gráfico
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="grafico-tooltip">
          <p className="tooltip-label">{label}</p>
          <p className="tooltip-valor">Ingresos: {formatearMoneda(payload[0].value)}</p>
          {payload[0].payload.nombreCompleto && (
            <p className="tooltip-nombre">{payload[0].payload.nombreCompleto}</p>
          )}
        </div>
      );
    }
    return null;
  };

  // Nueva función para calcular personas por mes
  const calcularPersonasPorMes = (reservas) => {
    // Inicializar objeto para almacenar número de personas por mes
    const personasPorMes = {};
    
    // Inicializar todos los meses con cero
    for (let i = 0; i < 12; i++) {
      personasPorMes[i] = 0;
    }

    // Calcular número de personas por mes del año actual
    reservas.forEach(reserva => {
      // Obtener fecha de entrada
      let fechaEntrada;
      if (reserva.fechaEntrada instanceof Timestamp) {
        fechaEntrada = reserva.fechaEntrada.toDate();
      } else if (typeof reserva.fechaEntrada === 'object' && reserva.fechaEntrada.seconds) {
        fechaEntrada = new Date(reserva.fechaEntrada.seconds * 1000);
      } else if (reserva.fechaEntrada instanceof Date) {
        fechaEntrada = reserva.fechaEntrada;
      } else {
        fechaEntrada = new Date(reserva.fechaEntrada || reserva.checkIn);
      }

      // Si la fecha es del año actual, sumar personas al mes correspondiente
      if (fechaEntrada.getFullYear() === anioActual) {
        const mes = fechaEntrada.getMonth();
        
        // Obtener número de personas
        let numPersonas = 0;
        
        // Caso 1: Reserva con múltiples habitaciones
        if (reserva.habitaciones && Array.isArray(reserva.habitaciones)) {
          // Sumar personas de todas las habitaciones
          numPersonas = reserva.habitaciones.reduce((sum, hab) => {
            return sum + (hab.personasEnHabitacion || hab.guests || 0);
          }, 0);
        } 
        // Caso 2: Reserva con una habitación
        else {
          numPersonas = reserva.personasEnHabitacion || reserva.guests || 1;
        }
        
        personasPorMes[mes] += parseInt(numPersonas);
      }
    });

    // Convertir a formato para recharts
    const datosParaGrafico = Object.keys(personasPorMes).map(mes => ({
      name: meses[mes],
      personas: personasPorMes[mes]
    }));

    setPersonasPorMes(datosParaGrafico);
  };

  // Personalizar tooltip del gráfico de personas
  const CustomTooltipPersonas = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="grafico-tooltip">
          <p className="tooltip-label">{label}</p>
          <p className="tooltip-valor">Personas: {payload[0].value}</p>
        </div>
      );
    }
    return null;
  };

  // Función para manejar el clic en una reserva
  const handleReservaClick = (reserva) => {
    // Navegar a AdminCalendario con el ID de la reserva
    navigate(`/calendario?editReservation=${reserva.id}`);
  };

  return (
    <div className="admin-dashboard">
      <h1>Dashboard Administrativo</h1>

      {/* Mensaje de carga/error */}
      {loading && <div className="message loading">Cargando datos...</div>}
      {error && <div className="message error">{error}</div>}

      {!loading && !error && (
        <div className="dashboard-container">
          {/* Reservas de hoy */}
          <div className="dashboard-card reservas-hoy">
            <div className="card-header">
              <h2>Reservas de Hoy - {formatearFecha(new Date())}</h2>
              <Link to="/calendario" className="ver-mas-btn">Ver calendario</Link>
            </div>
            <div className="card-content">
              {reservasHoy.length > 0 ? (
                <div className="tabla-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Hab.</th>
                        <th>Cliente</th>
                        <th>Evento</th>
                        <th>Personas</th>
                        <th>Contacto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reservasHoy.map(reserva => {
                        const habitacionId = reserva.habitacionId || reserva.roomId;
                        const personasEnHabitacion = reserva.personasEnHabitacion || reserva.guests || 1;
                        const nombre = reserva.nombre || reserva.name || 'Sin nombre';
                        const telefono = reserva.telefono || reserva.phone || '-';
                        const email = reserva.email || '-';
                        
                        return (
                          <tr 
                            key={reserva.id} 
                            className={reserva.tipoEvento === 'check-in' ? 'check-in' : reserva.tipoEvento === 'check-out' ? 'check-out' : 'estadia'}
                            onClick={() => handleReservaClick(reserva)}
                            style={{ cursor: 'pointer' }}
                            title="Haz clic para editar esta reserva"
                          >
                            <td className="habitacion-col">{habitacionId}</td>
                            <td>{nombre}</td>
                            <td className="evento-col">
                              <span className={`evento-badge ${reserva.tipoEvento}`}>
                                {reserva.tipoEvento === 'check-in' ? 'Entrada' : reserva.tipoEvento === 'check-out' ? 'Salida' : 'Estadia'}
                              </span>
                            </td>
                            <td className="personas-col">{personasEnHabitacion}</td>
                            <td className="contacto-col">
                              <div className="contacto-info">{telefono}</div>
                              <div className="contacto-info email">{email}</div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="mensaje-sin-datos">No hay reservas para hoy</div>
              )}
            </div>
          </div>

          {/* Reservas próxima semana */}
          <div className="dashboard-card reservas-semana">
            <div className="card-header">
              <h2>Próximas Reservas (7 días)</h2>
              <Link to="/calendario" className="ver-mas-btn">Ver calendario</Link>
            </div>
            <div className="card-content">
              {reservasSemana.length > 0 ? (
                <div className="tabla-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Hab.</th>
                        <th>Cliente</th>
                        <th>Duración</th>
                        <th>Personas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reservasSemana.map(reserva => {
                        const habitacionId = reserva.habitacionId || reserva.roomId;
                        const personasEnHabitacion = reserva.personasEnHabitacion || reserva.guests || 1;
                        const duracion = reserva.duracionEstancia || reserva.duration || 1;
                        const nombre = reserva.nombre || reserva.name || 'Sin nombre';
                        
                        return (
                          <tr key={reserva.id}>
                            <td>{formatearFecha(reserva.fechaEntrada || reserva.checkIn)}</td>
                            <td className="habitacion-col">{habitacionId}</td>
                            <td>{nombre}</td>
                            <td className="duracion-col">{duracion} {duracion === 1 ? 'noche' : 'noches'}</td>
                            <td className="personas-col">{personasEnHabitacion}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="mensaje-sin-datos">No hay reservas para los próximos 7 días</div>
              )}
            </div>
          </div>

          {/* Gráfico ingresos mensuales */}
          <div className="dashboard-card grafico-ingresos">
            <div className="card-header">
              <h2>Ingresos Mensuales {anioActual}</h2>
              <Link to="/finanzas" className="ver-mas-btn">Ver finanzas</Link>
            </div>
            <div className="card-content">
              {ingresosMensuales.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={ingresosMensuales}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(value) => `$${(value/1000)}k`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="ingresos" name="Ingresos" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="mensaje-sin-datos">No hay datos de ingresos para mostrar</div>
              )}
            </div>
          </div>

          {/* Gráfico ingresos por habitación */}
          <div className="dashboard-card grafico-habitaciones">
            <div className="card-header">
              <h2>Ingresos por Habitación - {meses[new Date().getMonth()]}</h2>
              <Link to="/finanzas" className="ver-mas-btn">Ver finanzas</Link>
            </div>
            <div className="card-content">
              {ingresosPorHabitacion.length > 0 ? (
                <div className="grafico-flex-container">
                  <div className="grafico-pie-container">
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={ingresosPorHabitacion}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="ingresos"
                        >
                          {ingresosPorHabitacion.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="grafico-tabla-container">
                    <table className="grafico-leyenda">
                      <thead>
                        <tr>
                          <th>Habitación</th>
                          <th>Ingresos ({meses[new Date().getMonth()]})</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ingresosPorHabitacion.map((entry, index) => (
                          <tr key={`legend-${index}`}>
                            <td>
                              <span className="color-dot" style={{ backgroundColor: COLORS[index % COLORS.length] }}></span>
                              {entry.name}
                            </td>
                            <td>{formatearMoneda(entry.ingresos)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="mensaje-sin-datos">No hay datos de ingresos por habitación este mes</div>
              )}
            </div>
          </div>

          {/* Gráfico de ocupación mensual por habitación */}
          <div className="dashboard-card">
            <div className="card-header">
              <h2>Ocupación Mensual por Habitación - {meses[new Date().getMonth()]}</h2>
            </div>
            <div className="card-content">
              {ocupacionPorHabitacion.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={ocupacionPorHabitacion}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="id" 
                      angle={0} 
                      textAnchor="middle"
                      height={50}
                      label={{ 
                        value: 'Habitaciones', 
                        position: 'insideBottomCenter',
                        dy: 15
                      }}
                    />
                    <YAxis 
                      label={{ 
                        value: 'Ocupación (%)', 
                        angle: -90, 
                        position: 'insideLeft' 
                      }}
                      domain={[0, 'auto']}
                      allowDecimals={false}
                    />
                    <Tooltip 
                      formatter={(value) => `${value}%`}
                      labelFormatter={(id) => {
                        const habitacion = ocupacionPorHabitacion.find(h => h.id == id);
                        return habitacion ? habitacion.nombre : `Habitación ${id}`;
                      }}
                    />
                    <Bar 
                      dataKey="ocupacion" 
                      fill="#82ca9d" 
                      name="Ocupación"
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="mensaje-sin-datos">No hay datos de ocupación para mostrar</div>
              )}
            </div>
          </div>

          {/* Nuevo gráfico de personas por mes */}
          <div className="dashboard-card grafico-personas">
            <div className="card-header">
              <h2>Número de Huéspedes por Mes {anioActual}</h2>
            </div>
            <div className="card-content">
              {personasPorMes.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={personasPorMes}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis 
                      allowDecimals={false}
                      label={{ 
                        value: 'Número de Huéspedes', 
                        angle: -90, 
                        position: 'insideLeft' 
                      }}
                    />
                    <Tooltip content={<CustomTooltipPersonas />} />
                    <Legend />
                    <Bar dataKey="personas" name="Huéspedes" fill="#ffc658" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="mensaje-sin-datos">No hay datos de huéspedes para mostrar</div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Menú flotante */}
      <FloatingMenu />
    </div>
  );
};

export default AdminDashboard; 